import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Camera, X } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (p, w) => `${BACKEND_URL}/api/files/${p.path}${w ? `?w=${w}` : ""}`;

const empty = { client_id: "", make: "", model: "", year: "", vin: "", plate: "", mileage: "", notes: "", photo: null };

export default function Vehicles() {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo: r.data }));
      toast.success("Фото завантажено");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const load = () => api.get("/vehicles", { params: { search } }).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

  const save = async () => {
    if (!form.client_id) return toast.error("Оберіть клієнта");
    const payload = { ...form, year: form.year ? Number(form.year) : null, mileage: form.mileage ? Number(form.mileage) : null };
    try {
      if (editId) await api.put(`/vehicles/${editId}`, payload);
      else await api.post("/vehicles", payload);
      toast.success("Збережено"); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const edit = (v) => { setForm({ ...v, year: v.year || "", mileage: v.mileage || "" }); setEditId(v.id); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Видалити авто?")) return; await api.delete(`/vehicles/${id}`); toast.success("Видалено"); load(); };

  const F = (k, label, type = "text") => (
    <div><Label>{label}</Label><Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`vehicle-${k}`} /></div>
  );

  return (
    <div>
      <PageHeader title="Автомобілі" subtitle={`Всього: ${list.length}`}
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild><Button data-testid="add-vehicle-btn"><Plus size={18} className="mr-1" /> Авто</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Редагувати авто" : "Нове авто"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Клієнт *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="vehicle-client"><SelectValue placeholder="Оберіть клієнта" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">{F("make", "Марка")}{F("model", "Модель")}</div>
                <div className="grid grid-cols-2 gap-3">{F("year", "Рік", "number")}{F("plate", "Держномер")}</div>
                {F("vin", "VIN")}
                {F("mileage", "Пробіг, км", "number")}
                <div>
                  <Label>Фотографія авто</Label>
                  <div className="mt-1.5">
                    {form.photo ? (
                      <div className="relative w-full h-40 rounded-md overflow-hidden border border-border bg-secondary" data-testid="vehicle-photo-preview">
                        <img src={photoUrl(form.photo, 800)} alt="Авто" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setForm({ ...form, photo: null })} data-testid="remove-vehicle-photo"
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center">
                          <X size={16} weight="bold" />
                        </button>
                      </div>
                    ) : (
                      <label data-testid="vehicle-photo-label"
                        className="flex flex-col items-center justify-center gap-1 h-40 rounded-md border-2 border-dashed border-border cursor-pointer text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                        <Camera size={28} />
                        <span className="text-xs">{uploading ? "Завантаження..." : "Завантажити фото (одне)"}</span>
                        <input type="file" accept="image/*" hidden disabled={uploading}
                          onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ""; }}
                          data-testid="vehicle-photo-input" />
                      </label>
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="save-vehicle-btn">Зберегти</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="relative mb-4 max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Пошук за номером / VIN / маркою..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="vehicles-search" />
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Авто</TableHead><TableHead>Держномер</TableHead><TableHead>VIN</TableHead>
            <TableHead>Пробіг</TableHead><TableHead>Власник</TableHead><TableHead className="text-right">Дії</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((v) => (
              <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                <TableCell className="font-semibold">
                  <div className="flex items-center gap-2">
                    {v.photo ? (
                      <img src={photoUrl(v.photo, 120)} alt="" loading="lazy" decoding="async" className="w-9 h-9 rounded object-cover border border-border" />
                    ) : (
                      <div className="w-9 h-9 rounded bg-secondary border border-border flex items-center justify-center text-muted-foreground"><Camera size={16} /></div>
                    )}
                    <span>{v.make} {v.model} <span className="text-muted-foreground font-normal">{v.year}</span></span>
                  </div>
                </TableCell>
                <TableCell>{v.plate || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{v.vin || "—"}</TableCell>
                <TableCell>{v.mileage ? `${v.mileage} км` : "—"}</TableCell>
                <TableCell>{v.client_name}</TableCell>
                <TableCell className="text-right">
                  <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => edit(v)} data-testid={`edit-vehicle-${v.id}`}><PencilSimple size={18} /></button>
                  <button className="text-muted-foreground hover:text-destructive p-1" onClick={() => del(v.id)} data-testid={`del-vehicle-${v.id}`}><Trash size={18} /></button>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Немає авто</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
